import React, { useState, useEffect, useRef, act } from "react";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import ConsignmentModal from "./ConsignmentModal";
import { normalizeDateTime, toDateTimeLocal } from "../../utils/datetime";
import { createTrip, getTrip, getManifestDownloadUrl, updateTripPartial, uploadManifest, uploadConsignmentDocuments, getConsignmentDocumentDownloadUrl, deleteConsignmentDocument } from "../../services/trips";
import { USER_PROFILE, USER_ROLE } from '../../services/auth';
import { downloadFile, downloadLocalFile } from '../../utils/fileDownload';
import { useBranches } from '../../context/branches';
import { useBranchUsers } from '../../context/branch_users';
import { useClients } from '../../context/clients';
import { useCustomers } from '../../context/customers';
import { useDrivers } from '../../context/drivers';
import { useTripTypes } from "../../context/trips";
import { useVehicles, useVehicleTypes } from "../../context/vehicles";
import Spinner from '../../components/ui/Spinner';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import BillingSection from "./sections/BillingSection";
import ConsignmentSection from "./sections/ConsignmentSection";
import DriverSection from "./sections/DriverSection";
import LocationSection from "./sections/LocationSection";
import TimelineSection from "./sections/TimelineSection";
import TripDetailsSection from "./sections/TripDetailsSection";
import VehicleSection from "./sections/VehicleSection";
import FormStepper from "../ui/FormStepper";
import CustomDataSection from "./sections/CustomDataSection";
import RequestorSection from "./sections/RequestorSection";
import { useUnsavedChanges } from "../../context/UnsavedChangesContext";
import { toast } from 'react-toastify';


const fixedCharges = [
  "Freight Charge",
  "Detention Charge",
  "Parking Charge",
  "Loading Charge",
  "Unloading Charge",
  "Toll Charge",
];

const VEHICLE_NUMBER_REGEX = /^[A-Z]{2}\d{1,2}(?:[A-Z]{1,2})?\d{1,4}$/;

const defaultCharges = [
  { name: "Freight Charge", unit: 1, price_per_unit: 0, total: 0 },
  { name: "Detention Charge", unit: 1, price_per_unit: 0, total: 0 },
  { name: "Parking Charge", unit: 1, price_per_unit: 0, total: 0 },
  { name: "Loading Charge", unit: 1, price_per_unit: 0, total: 0 },
  { name: "Unloading Charge", unit: 1, price_per_unit: 0, total: 0 },
  { name: "Toll Charge", unit: 1, price_per_unit: 0, total: 0 },
];

const driverModes = [
  { "id": "own", "value": "Own" },
  { "id": "market", "value": "market" },
];

const vehicleModes = [
  { "id": "own", "value": "Own" },
  { "id": "market", "value": "market" },
];

const FORM_STEPS = [
  { id: "vehicle", label: "Vehicle" },
  { id: "driver", label: "Driver" },
  { id: "location", label: "Location" },
  { id: "timeline", label: "Timeline" },
  { id: "requestor", label: "Request" },
  { id: "custom_data", label: "Custom Data"},
  { id: "consignment", label: "Consignments" },
  { id: "billing", label: "Billing" },
];

const STEP_FIELD_MAP = {
  vehicle: [
    'vehicle_mode', 'requested_vehicle_id', 'assigned_vehicle_id', 'vehicle_number',
    'vehicle_type_id', 'vehicle_capacity', 'vendor_name', 'requested_vehicle',
    'assigned_vehicle', 'vehicle_type',
  ],
  driver: [
    'driver_mode', 'driver_id', 'driver_name', 'driver_contact', 'driver',
  ],
  location: [
    'from', 'to', 'from_location', 'to_location', 'client_id',
  ],
  timeline: [
    'booking_date', 'booking_time', 'placement_pickup_date', 'placement_pickup_time',
    'expected_pickup_date', 'expected_pickup_time', 'expected_delivery_date',
    'expected_delivery_time', 'actual_in_pickup_date', 'actual_in_pickup_time',
    'actual_out_pickup_date', 'actual_out_pickup_time', 'actual_in_delivery_date',
    'actual_in_delivery_time', 'actual_out_delivery_date', 'actual_out_delivery_time',
  ],
  requestor: [
    'loading_supervisor_id', 'loading_supervisor_name', 'loading_supervisor_contact',
    'requested_by', 'type', 'type_other',
  ],
  custom_data: [
    'custom_fields', 'custom_field_values',
  ],
  consignment: [
    'consignment_groups', 'consignments', 'hwb_number', 'total_boxes', 'total_weight', 'remarks', 'manifest',
  ],
  billing: [
    'charges', 'freight_charge', 'detention_charge', 'parking_charge', 'loading_charge',
    'unloading_charge', 'toll_charge',
  ],
};

const TripForm = ({ trip, onSave, onCancel, readOnly = false }) => {

  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));
  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role == 'admin');
  const showLoadingSupervisor = ['admin', 'branch_admin'].includes(role);
  const isBranchUser = role === 'branch_user';

  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const timeNow = now.toTimeString().slice(0, 5);
  const defaultValues = {
    id: 0,
    customer: {},
    customer_id: 0,
    client: {},
    client_id: 0,
    type: "",
    type_other: "",
    requested_vehicle: {},
    requested_vehicle_id: 0,
    assigned_vehicle: {},
    assigned_vehicle_id: "",
    vendor_name: "",
    vehicle_mode: "own",
    vehicle_number: "",
    vehicle_type: {},
    vehicle_type_id: 0,
    vehicle_capacity: "",
    driver_mode: "own",
    driver: {},
    driver_id: "",
    driver_name: "",
    driver_contact: "",
    requested_by: "",
    from: 0,
    to: 0,
    from_location: {},
    to_location: {},
    booking_date: today,
    booking_time: timeNow,
    placement_pickup_date: today,
    placement_pickup_time: timeNow,
    expected_pickup_date: "",
    expected_pickup_time: "",
    expected_delivery_date: "",
    expected_delivery_time: "",
    actual_in_pickup_date: "",
    actual_in_pickup_time: "",
    actual_out_pickup_date: "",
    actual_out_pickup_time: "",
    actual_in_delivery_date: "",
    actual_in_delivery_time: "",
    actual_out_delivery_date: "",
    actual_out_delivery_time: "",
    vehicle_selection_remarks: "",
    consignment_groups: [],
    branch_id: profile?.branch?.id || 0,
    branch: profile?.branch || {},
    loading_supervisor_id: 0,
    loading_supervisor: {},
    loading_supervisor_name: role === 'branch_user' ? profile?.name : "",
    loading_supervisor_contact: role === 'branch_user' ? profile?.phone : "",
    custom_fields: [],
    charges: defaultCharges,
  };

  const [activeStep, setActiveStep] = useState(0);
  const [tripCreated, setTripCreated] = useState(!!trip?.id);
  const [showConsignmentModal, setShowConsignmentModal] = useState(false);
  const [expandedClients, setExpandedClients] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(() => {
    return trip ? {
      ...defaultValues,
      ...trip,
    } : defaultValues;
  });
  const [manifestFiles, setManifestFiles] = useState({});
  // Map of tmp_id → File[] for consignments not yet saved to DB.
  // Drained after each successful trip save once we get real consignment IDs.
  const pendingDocUploads = useRef({});
  const { setIsDirty, registerConfirm } = useUnsavedChanges();

  const dirtyRef = useRef(false);
  const isInitializingRef = useRef(true);
  const customFieldsInitRef = useRef(false);


  let branches = [], branch_loading = false, refreshBranches = null;
  if (showBranches) {
    const result = useBranches({ reload: true, params: { status: 'active', paginate: false } });
    branches = result.branches;
    branch_loading = result.loading;
    refreshBranches = result.refresh;
  }
  let branchUsers = [], branch_users_loading = false, refreshBranchUsers = null;
  let branchAdmins = [], branch_admins_loading = false, refreshBranchAdmins = null;
  let loading_supervisors = [], result_users = [], result_admins = [];

  if (role === 'admin') {
    result_admins = useBranchUsers({ reload: true, params: { status: 'active', user_type: "branch_admin", paginate: false } });
    branchAdmins = result_admins.branchUsers;
    branch_admins_loading = result_admins.loading;
    refreshBranchAdmins = result_admins.refresh;
  }

  if (showLoadingSupervisor || (role === 'admin')) {
    result_users = useBranchUsers({ reload: true, params: { status: 'active', user_type: "branch_user", paginate: false } });
    branchUsers = result_users.branchUsers;
    branch_users_loading = result_users.loading;
    refreshBranchUsers = result_users.refresh;
  }

  if (role === 'branch_admin') {
    loading_supervisors = [profile, ...branchUsers].filter((value, index, self) =>
      value?.id && index === self.findIndex(item => item?.id === value?.id)
    );
  } else if (role == 'admin') {
    loading_supervisors = [...branchAdmins, ...branchUsers];
  } else {
    loading_supervisors = [profile];
  }

  const { clients, loading: client_loading, refresh: refreshClients } = useClients({ reload: true, params: { status: 'active', paginate: false } });
  const { customers, loading: customer_loading, refresh: refreshCustomers } = useCustomers({ reload: true, params: { status: 'active', paginate: false } });
  const { vehicleTypes, loading: vehicle_type_loading, refresh: refreshVehicleTypes } = useVehicleTypes({ reload: true });
  const { drivers, loading: driver_loading, refresh: refreshDrivers } = useDrivers({ reload: true, params: { status: 'active', paginate: false } });
  const { tripTypes, loading: triptype_loading, refresh: refreshTripTypes } = useTripTypes({ reload: true });
  const { vehicles, loading: vehicle_loading, refresh: refreshVehicles } = useVehicles({ reload: true, params: { status: 'active', paginate: false } });
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
    onCancel: () => setConfirmModalParams({...confirmModalParams, open: false})
  });

  const loadLoadingSupervisorData = async () => {
    if (!showLoadingSupervisor) return;
    await Promise.all([
      refreshBranchUsers ? refreshBranchUsers() : Promise.resolve(),
      refreshBranchAdmins ? refreshBranchAdmins() : Promise.resolve(),
    ]);
  };

  const markFixed = (values) => {
    for (let idx = 0; idx < values.length; idx++) {
      values[idx]['fixed'] = (fixedCharges.filter((c) => c == values[idx]['name']).length > 0)
    }
    return values;
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
    shouldUnregister: false,
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState,
  } = methods;


  const assignedVehicle = watch("assigned_vehicle_id");
  const requestedVehicle = watch("requested_vehicle_id");
  const vehicleType = watch("vehicle_type_id");
  const vehicle_mode = watch("vehicle_mode");
  const driver_mode = watch("driver_mode");
  const driver = watch("driver_id");
  const branch = watch("branch_id");
  const loading_supervisor = watch("loading_supervisor_id");
  const customer_id = watch("customer_id");
  const client_id = watch("client_id");
  const from_location = watch("from");
  const to_location = watch("to");
  const charges = watch("charges");
  const consignment_groups = watch("consignment_groups");
  const formTripId = watch("id");  // reactive — updates after reset(fresh)
  // isEditMode must be after formTripId — includes trips auto-created mid-session
  const isEditMode = Boolean(trip?.id) || formTripId > 0;

  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [filteredClients, setFilteredClients] = useState(clients);
  const [filteredDrivers, setFilteredDrivers] = useState(drivers);
  const [filteredVehicles, setFilteredVehicles] = useState(vehicles);
  const [editingConsigment, setEditingConsigment] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [customFieldValues, setCustomFieldValues] = useState([]);

  const handleUppercaseInputCapture = (e) => {
    const element = e?.target;
    if (!element || element.readOnly || element.disabled) return;
    if (element.dataset?.case === 'normal') return;

    const tag = (element.tagName || '').toLowerCase();
    const type = (element.type || '').toLowerCase();
    const isSupported = tag === 'textarea' || (tag === 'input' && ['text', 'search'].includes(type));
    if (!isSupported) return;

    const upper = String(element.value || '').toUpperCase();
    if (upper !== element.value) {
      element.value = upper;
    }
  };

  const flattenBackendErrors = (errorObj) => {
    const messages = [];
    const visit = (value, prefix = '') => {
      if (Array.isArray(value)) {
        value.forEach((item) => visit(item, prefix));
        return;
      }
      if (value && typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          const nextPrefix = prefix ? `${prefix}.${k}` : k;
          visit(v, nextPrefix);
        });
        return;
      }
      if (value !== null && value !== undefined && String(value).trim()) {
        messages.push(prefix ? `${prefix}: ${value}` : String(value));
      }
    };
    visit(errorObj);
    return messages;
  };

  const getErrorFieldKeys = (errorObj) => {
    if (!errorObj || typeof errorObj !== 'object') return [];
    return Object.keys(errorObj);
  };

  const resolveStepIndexFromErrors = (errorObj) => {
    const keys = getErrorFieldKeys(errorObj);
    for (let i = 0; i < FORM_STEPS.length; i++) {
      const stepId = FORM_STEPS[i].id;
      const stepFields = STEP_FIELD_MAP[stepId] || [];
      const hasStepError = keys.some((key) => {
        const baseKey = String(key || '').split('.')[0];
        return stepFields.includes(baseKey);
      });
      if (hasStepError) return i;
    }
    return -1;
  };

  const showBackendErrorToast = (errorObj) => {
    const messages = flattenBackendErrors(errorObj);
    if (!messages.length) {
      toast.error('Unable to save trip. Please check entered values.');
      return;
    }
    const visibleMessages = messages.slice(0, 5);
    const remaining = messages.length - visibleMessages.length;
    toast.error(
      <div>
        <div className="font-semibold mb-1">Please fix the following errors:</div>
        <ul className="list-disc ml-4">
          {visibleMessages.map((msg, idx) => (
            <li key={`${msg}-${idx}`}>{msg}</li>
          ))}
        </ul>
        {remaining > 0 && (
          <div className="mt-1 text-xs">+{remaining} more error(s)</div>
        )}
      </div>,
      { autoClose: 5000 }
    );
  };


  const {
    fields: billingFields,
    append: appendBilling,
    remove: removeBilling,
  } = useFieldArray({ control, name: "charges" });

  const { 
    fields: customFields,
    append: appendCustomField,
    replace: replaceCustomFields
  } = useFieldArray({
    control,
    name: "custom_fields"
  });

  const totalCharge = charges.reduce(
    (sum, b) => sum + b.unit * b.price_per_unit,
    0
  );

  const totalConsignmentCount = consignment_groups.reduce(
    (count, group) => count + (group?.consignments?.length || 0), 0
  );

  const nextStep = () => {
    if (activeStep < FORM_STEPS.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(prev => prev - 1);
    }
  };

  useEffect(() => {
    setCustomFieldValues(trip?.custom_field_values || []);
  }, [trip]);

  useEffect(() => {
    const handler = (e) => {
      if (!formState.isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    dirtyRef.current = formState.isDirty;
    setIsDirty(formState.isDirty);
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [formState.isDirty]);

  useEffect(() => {
    registerConfirm(confirmIfDirty);
    return () => registerConfirm(null);
  }, []);

  useEffect(() => {
    onBranchChange();
  }, [branch, customers, drivers, vehicles, clients, formTripId, customer_id]);

  useEffect(() => {
    loadLoadingSupervisorData();
  }, [showLoadingSupervisor, branch]);

  useEffect(() => {
    if (!showLoadingSupervisor) return;
    onLoadingSupervisorChange();
  }, [loading_supervisor, branchUsers, branchAdmins, showLoadingSupervisor]);

  useEffect(() => {

    if (!clients || clients?.length <= 0) return;
    if (!customers || customers.length <= 0) return;
    if (!vehicleTypes || vehicleTypes?.length <= 0) return;
    if (!drivers || drivers?.length <= 0) return;
    if (!tripTypes || tripTypes?.length <= 0) return;
    if (!vehicles || vehicles?.length <= 0) return;

    if (!trip) return;

    isInitializingRef.current = true;

    // Fetch full trip detail asynchronously so nested documents are included.
    // The reset below uses `tripData` which defaults to the passed prop but
    // is replaced by the full API response once it arrives.
    const initForm = async () => {
      let tripData = trip;
      if (trip.id) {
        const [, fullTrip] = await getTrip(trip.id);
        if (fullTrip) {
          tripData = fullTrip;
          setInitialData({ ...defaultValues, ...fullTrip });
        }
      }

      const customer = customers.find(c => c.id === Number(tripData?.customer?.id || tripData?.customer_id || customer_id));
      const hasManualSupervisorDetails = Boolean(
        tripData?.loading_supervisor_name || tripData?.loading_supervisor_contact
      );
      const resolvedSupervisorId = showLoadingSupervisor
        ? (tripData?.loading_supervisor?.id ?? (hasManualSupervisorDetails ? -1 : 0))
        : 0;

      reset({
        ...defaultValues,
        ...tripData,
        branch_id: tripData?.branch?.id || profile?.branch?.id || 0,
        loading_supervisor_id: resolvedSupervisorId,
        loading_supervisor_name: (showLoadingSupervisor || isBranchUser) ? (tripData?.loading_supervisor_name ?? tripData?.loading_supervisor?.name ?? profile?.name ?? "") : "",
        loading_supervisor_contact: (showLoadingSupervisor || isBranchUser) ? (tripData?.loading_supervisor_contact ?? tripData?.loading_supervisor?.phone ?? profile?.phone ?? "") : "",
        type: tripData?.type || "",
        customer_id: tripData?.customer?.id || 0,
        client_id: tripData?.client?.id || 0,
        driver_id: String(tripData?.driver?.id) || "",
        requested_vehicle_id: tripData?.requested_vehicle?.id || 0,
        vehicle_type_id: tripData?.vehicle_type?.id || 0,
        assigned_vehicle_id: String(tripData?.assigned_vehicle?.id) || "",
        booking_date: tripData?.booking_date ?? today,
        booking_time: tripData?.booking_time ?? timeNow,
        placement_pickup_date: tripData?.placement_pickup_date ?? today,
        placement_pickup_time: tripData?.placement_pickup_time ?? timeNow,
        expected_pickup_date: tripData?.expected_pickup_date ?? "",
        expected_pickup_time: tripData?.expected_pickup_time ?? "",
        expected_delivery_date: tripData?.expected_delivery_date ?? "",
        expected_delivery_time: tripData?.expected_delivery_time ?? "",
        actual_in_pickup_date: tripData?.actual_in_pickup_date ?? "",
        actual_in_pickup_time: tripData?.actual_in_pickup_time ?? "",
        actual_out_pickup_date: tripData?.actual_out_pickup_date ?? "",
        actual_out_pickup_time: tripData?.actual_out_pickup_time ?? "",
        actual_in_delivery_date: tripData?.actual_in_delivery_date ?? "",
        actual_in_delivery_time: tripData?.actual_in_delivery_time ?? "",
        actual_out_delivery_date: tripData?.actual_out_delivery_date ?? "",
        actual_out_delivery_time: tripData?.actual_out_delivery_time ?? "",
        from: tripData?.from_location?.tid,
        to: tripData?.to_location?.tid,
        custom_fields: customer ? customFieldsWithValues(customer) : [],
        charges: markFixed(
          (tripData?.charges && tripData.charges.length > 0)
            ? tripData.charges
            : defaultCharges
        ),
      });
      onBranchChange(
        tripData?.branch?.id || profile?.branch?.id,
        tripData?.customer?.id
      );
      // isInitializingRef.current = false;
      requestAnimationFrame(() => {
        isInitializingRef.current = false;
      }, 0);
    };

    initForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, clients, vehicleTypes, drivers, tripTypes, vehicles, trip]);

  // useEffect(() => {
  //   console.log("Dirty fields:", formState.dirtyFields);
  //   console.log("isDirty:", formState.isDirty);
  // }, [formState]);

  useEffect(() => {
    onAssignVehicleChange();
  }, [assignedVehicle]);

  useEffect(() => {
    onDriverChange();
  }, [driver]);

  useEffect(() => {
    onRequestVehicleChange();
  }, [requestedVehicle]);

  useEffect(() => {
    onFromLocationChange(from_location);
  }, [from_location]);

  useEffect(() => {
    onToLocationChange(to_location);
  }, [to_location]);

  useEffect(() => {
    if (isInitializingRef.current) return;
    setVehicleDetails(null, "");
  }, [vehicle_mode]);

  useEffect(() => {
    if (isInitializingRef.current) return;
    setDriverDetails(null, "");
  }, [driver_mode]);

  useEffect(() => {
    customFieldsInitRef.current = false;
  }, [customer_id, trip?.id]);

  useEffect(() => {
    if (customFieldsInitRef.current) return;

    if (!customer_id) return;
    if (!customers.length) return;

    const customer = customers.find(c => c.id === Number(customer_id));
    if (!customer?.custom_fields?.length) {
      replaceCustomFields([]);
      return;
    }

    const fieldsWithValues = customFieldsWithValues(customer);

    replaceCustomFields(fieldsWithValues);
    reset({
      ...defaultValues,
      ...getValues(),
      customer_id: trip?.customer?.id || customer_id,
      fieldsWithValues,
    });

    customFieldsInitRef.current = true;
  }, [customer_id, customers]);

  const customFieldsWithValues = (c) => {
    return c.custom_fields.map(field => {
      const saved = customFieldValues.find(
        v => v.custom_field === field.id
      );

      return {
        id: field.id,
        name: field.name,
        field_type: field.field_type,
        mandatory: field.mandatory,
        value: saved?.value ?? "",
      };
    });
  };

  const confirmIfDirty = (action) => {
    if (!dirtyRef.current) {
      action();
      return;
    }

    setConfirmModalParams({
      open: true,
      title: "Unsaved Changes",
      message: "You have unsaved changes. Do you want to save before leaving?",
      confirmText: "Save",
      cancelText: "Discard",
      onConfirm: async (e) => {
        e.preventDefault();
        await handleSubmit(handleTripSubmission)();
        setConfirmModalParams(p => ({ ...p, open: false }));
        action();
      },
      onCancel: () => {
        reset(initialData);
        dirtyRef.current = false;
        setIsDirty(false);
        setConfirmModalParams(p => ({ ...p, open: false }));
        action();
      },
    });
  };

  const onBranchChange = (overrideBranch, overrideCustomerId) => {
    const effectiveBranch = overrideBranch ?? branch;
    const effectiveCustomerId = overrideCustomerId ?? customer_id;
    const fDrivers = (effectiveBranch && effectiveBranch != 0)
      ? drivers.filter(d => d.branch.id === Number(effectiveBranch))
      : [];
    let fCustomers = (effectiveBranch && effectiveBranch != 0)
      ? customers.filter(c => c.branch.id === Number(effectiveBranch))
      : [];
    let fClients = [];
    if (isEditMode && effectiveCustomerId) {
      const selectedCustomer = customers.find(
        c => c.id === Number(effectiveCustomerId)
      );
      if (selectedCustomer) {
        if (!fCustomers.some(c => c.id === selectedCustomer.id)) {
          fCustomers = [selectedCustomer, ...fCustomers];
        }
        fClients = clients.filter(cl => selectedCustomer?.clients?.includes(cl.id));
      }
    }
    setFilteredCustomers(fCustomers);
    setFilteredClients(fClients);
    setFilteredDrivers(fDrivers);
    updateFilteredVehicles(effectiveBranch, requestedVehicle);
  };

  const onLoadingSupervisorChange = () => {
    const supervisorId = Number(loading_supervisor);

    if (supervisorId === -1) {
      return;
    }

    const lsupervisor = supervisorId > 0
      ? loading_supervisors.filter(ls => ls.id === supervisorId)
      : [];
    const selectedSupervisor = lsupervisor.length > 0 ? lsupervisor[0] : null;

    setValue("loading_supervisor_name", selectedSupervisor?.name || "", {
      shouldDirty: !isInitializingRef.current,
    });
    setValue("loading_supervisor_contact", selectedSupervisor?.phone || "", {
      shouldDirty: !isInitializingRef.current,
    });
  };

  const onAssignVehicleChange = () => {
    const selected = vehicles.find(v => v.id === Number(assignedVehicle));
    setVehicleDetails(selected, assignedVehicle);
  };

  const onRequestVehicleChange = () => {
    updateFilteredVehicles(branch, requestedVehicle);
  };

  const updateFilteredVehicles = (branchId, requestedVehicleId) => {
    const fVehicles = (branchId && branchId != 0)
      ? vehicles.filter(v => (v.branch.id === Number(branchId)))
      : [];
    setFilteredVehicles(fVehicles);
  };

  const onFromLocationChange = (code) => {
    if (code == "0") {
        setLocation("from_location", null);
    } else {
      const selected = locations.filter(l => l.tid === code);
      if (selected && selected.length > 0) {
        setLocation("from_location", { ...selected[0] });
      }
    }
  };

  const onToLocationChange = (code) => {
    if (code == "0") {
      setLocation("to_location", null);
    } else {
      const selected = locations.filter(l => l.tid === code);
      if (selected && selected.length > 0) {
        setLocation("to_location", { ...selected[0] });
      }
    }
  };

  const setLocation = (key, selected) => {
    if (selected == null) {
      setValue(key, {
        id: 0,
        tid: "0",
        short_code: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        pincode: "",
        code: 0,
        full_address: "",
        delivery_type: ""
      });
    } else {
      setValue(key, {
        short_code: selected.short_code,
        tid: selected.tid,
        address_line1: selected.address_line1,
        address_line2: selected.address_line2,
        city: selected.city,
        state: selected.state,
        code: selected.customer_code || selected.client_code,
        id: selected.id,
        full_address: selected.full_address,
        pincode: selected.pincode,
        delivery_type: selected.delivery_type || ''
      });
    }
  };

  const onDriverChange = () => {
    const selected = drivers.find(d => d.id === Number(driver));
    setDriverDetails(selected, driver);
  };

  const setVehicleDetails = (selected, assigned_vehicle_id) => {
    if (assigned_vehicle_id) {
      setValue("vehicle_number", selected?.vehicle_number || "", {
        shouldDirty: !isInitializingRef.current,
      });
      setValue("vehicle_type", selected?.vehicle_type?.name, {
        shouldDirty: !isInitializingRef.current,
      });
    } else {
      setValue("vehicle_number", "", {
        shouldDirty: !isInitializingRef.current,
      });
      setValue("vehicle_type", "", {
        shouldDirty: !isInitializingRef.current,
      });
    }
    setValue("vehicle_capacity", "", {
      shouldDirty: !isInitializingRef.current,
    });
  };

  const setDriverDetails = (selected, driver_id) => {
    if (driver_id) {
      setValue("driver_name", selected?.name, {
        shouldDirty: !isInitializingRef.current,
      });
      setValue("driver_contact", selected?.primary_phone, {
        shouldDirty: !isInitializingRef.current,
      });
    } else {
      setValue("driver_name", "", {
        shouldDirty: !isInitializingRef.current,
      });
      setValue("driver_contact", "", {
        shouldDirty: !isInitializingRef.current,
      });
    }
  };

  // Consignment helpers
  const removeConsignmentFromGroups = (updatedGroups, consignment) => {
    return updatedGroups.map(group => {
      const updatedConsignments = group.consignments.filter(c => {
        if (consignment.id > 0) {
          return c.id !== consignment.id;
        } else {
          return c.tmp_id !== consignment.tmp_id;
        }
      });

      return {
        ...group,
        consignments: updatedConsignments,
      };
    }).filter(group => group.consignments.length > 0); // optional: remove empty groups
  };

  const findGroupIdxByConsignment = (groups, consignment) => {
    return groups.findIndex(group =>
      group.consignments.some(c =>
        consignment.id > 0
          ? Number(c.id) === Number(consignment.id)
          : c.tmp_id === consignment.tmp_id
      )
    );
  };

  const handleConsignmentAdd = async (newConsignment) => {
    // Extract staged files before putting the consignment into form state.
    const stagedFiles = newConsignment._stagedFiles || [];
    const { _stagedFiles, ...consignmentData } = newConsignment;

    if (stagedFiles.length > 0 && consignmentData.tmp_id) {
      pendingDocUploads.current[consignmentData.tmp_id] = [
        ...(pendingDocUploads.current[consignmentData.tmp_id] || []),
        ...stagedFiles,
      ];
    }

    const currentGroups = getValues("consignment_groups") || [];
    const clientId = consignmentData.client;
    const clientName = consignmentData.client_name;

    let updatedGroups = [...currentGroups];
    const newGroupIdx = updatedGroups.findIndex(group => group.client == clientId);
    const newGroup = newGroupIdx > -1 ? updatedGroups[newGroupIdx] : null;
    const existingGroupIdx = findGroupIdxByConsignment(updatedGroups, consignmentData);
    const existingGroup = existingGroupIdx > -1 ? updatedGroups[existingGroupIdx] : null;
    if (existingGroup) {
      const consignments = existingGroup.consignments;
      if (existingGroup.client == newGroup?.client) {
        if (consignmentData.id > 0) {
          const existingIndex = consignments.findIndex(c => c.id === consignmentData.id);
          if (existingIndex !== -1) {
            consignments[existingIndex] = { ...consignments[existingIndex], ...consignmentData };
          } else {
            consignments.push(consignmentData);
          }
        } else if (consignmentData.tmp_id) {
          const existingIndex = consignments.findIndex(c => c.tmp_id === consignmentData.tmp_id);
          if (existingIndex !== -1) {
            consignments[existingIndex] = { ...consignments[existingIndex], ...consignmentData };
          } else {
            consignments.push(consignmentData);
          }
        }
      } else {
        updatedGroups = removeConsignmentFromGroups(updatedGroups, consignmentData);
        if (newGroup) {
          newGroup.consignments.push(consignmentData);
          updatedGroups[newGroupIdx] = newGroup;
        } else {
          updatedGroups.push({
            client: clientId,
            client_name: clientName,
            consignments: [consignmentData]
          });
        }
      }
    } else {
      if (newGroup) {
        newGroup.consignments.push(consignmentData);
        updatedGroups[newGroupIdx] = newGroup;
      } else {
        updatedGroups.push({
          client: clientId,
          client_name: clientName,
          consignments: [consignmentData]
        });
      }
    }

    setValue("consignment_groups", updatedGroups, { shouldDirty: true });

    // Auto-save the trip immediately so the consignment gets a real DB id,
    // then drain any staged doc uploads that were attached in the modal.
    // Toast and modal-close happen AFTER this completes so the modal's
    // isSaving state blocks navigation until everything is persisted.
    try {
      const currentFormValues = { ...getValues(), consignment_groups: updatedGroups };
      const cleaned = cleanupInputData(currentFormValues);

      // Build a map of tmp_id → hwb_number BEFORE saving, so we can match
      // consignments in the server response (which never returns tmp_id).
      const tmpIdToHwb = {};
      for (const [tmpId, files] of Object.entries(pendingDocUploads.current)) {
        for (const group of updatedGroups) {
          for (const c of group.consignments || []) {
            if (c.tmp_id === tmpId) {
              tmpIdToHwb[tmpId] = c.hwb_number;
            }
          }
        }
      }

      const result = await saveData(cleaned);
      // saveData returns undefined when getChangedFields finds nothing to patch
      if (!result) {
        toast.success("Consignment Saved Successfully.");
        if (editingConsigment) setShowConsignmentModal(false);
        return;
      }
      const [saveErr, savedTrip] = result;
      if (saveErr) {
        setErrors(saveErr);
        throw new Error("Trip save failed");
      }
      if (savedTrip) {
        // Drain pending uploads — match by hwb_number since tmp_id is not in the API response.
        const pending = pendingDocUploads.current;
        const uploads = [];
        for (const group of (savedTrip.consignment_groups || [])) {
          for (const c of (group.consignments || [])) {
            for (const [tmpId, files] of Object.entries(pending)) {
              if (
                files.length > 0 &&
                c.id > 0 &&
                tmpIdToHwb[tmpId] &&
                c.hwb_number === tmpIdToHwb[tmpId]
              ) {
                uploads.push(uploadConsignmentDocuments(savedTrip.id, c.id, files));
                delete pending[tmpId];
              }
            }
          }
        }
        if (uploads.length > 0) await Promise.all(uploads);

        const [, fresh] = await getTrip(savedTrip.id);
        if (fresh) {
          setInitialData(fresh);
          resetFromServerResponse(fresh);
          setValue('id', fresh.id, { shouldDirty: false, shouldTouch: false });
          setValue('consignment_groups', fresh.consignment_groups || [], { shouldDirty: false, shouldTouch: false });
          dirtyRef.current = false;
          setIsDirty(false);
        }
      }

      // Only show success and close AFTER everything is persisted
      toast.success("Consignment Saved Successfully.");
      if (editingConsigment) setShowConsignmentModal(false);
    } catch (e) {
      // cleanupInputData can throw if the form is in an incomplete state
      // (e.g. no customer selected yet). In that case skip the auto-save —
      // show success for the local save and let the user save the full trip manually.
      console.warn("Auto-save after consignment add skipped:", e.message);
      toast.success("Consignment Saved Successfully.");
      if (editingConsigment) setShowConsignmentModal(false);
    }
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};

    // Treat null / undefined / "" as the same "empty" value so that optional
    // date/time fields that were never filled don't get sent to the API.
    const isEmpty = (v) => v === null || v === undefined || v === "";

    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (key === 'charges') {
          // charges always sent so server can sync the full list
          changed[key] = updated[key];
        } else if (key === 'consignment_groups') {
          // Only send if something actually changed — avoids unnecessary server round-trips.
          // Strip read-only / frontend-only fields before comparing so that a fresh
          // reset(serverResponse) doesn't look different from the current form state
          // just because the server added `documents` or the frontend had `tmp_id`.
          const stripTransient = (groups) =>
            (groups || []).map(g => ({
              ...g,
              consignments: (g.consignments || []).map(({ tmp_id, documents, ...c }) => c),
            }));
          if (JSON.stringify(stripTransient(initial[key])) !== JSON.stringify(stripTransient(updated[key]))) {
            changed[key] = updated[key];
          }
        } else {
          if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
            changed[key] = updated[key];
          }
        }
      } else {
        // Handle datetime comparison
        if (
          ['expected_pickup', 'expected_delivery'].includes(key) &&
          normalizeDateTime(initial[key]) === normalizeDateTime(updated[key])
        ) {
          continue; // Skip unchanged
        }

        const initialVal = initial[key];
        const updatedVal = updated[key];

        // Both empty → nothing changed, skip
        if (isEmpty(initialVal) && isEmpty(updatedVal)) continue;

        if (updatedVal !== initialVal) {
          // Send null only when clearing a previously-set value; omit if both were empty
          changed[key] = isEmpty(updatedVal) ? null : updatedVal;
        }
      }
    }
    return changed;
  };

  const normalizeEmpty = (v) => {
    return v === "" || v === null || v === undefined ? undefined : v;
  }

  const cleanupInputData = (data) => {
    const normalizedVehicleNumber = String(data.vehicle_number || "")
      .toUpperCase()
      .replace(/\s+/g, "");
    const selectedCustomer = getSelectedCustomer();
    const supervisorId = Number(data.loading_supervisor_id);
    // Guard: selectedCustomer may be null when form is partially filled
    const custom_field_values = (selectedCustomer?.custom_fields || []).map((field, idx) => {
      return {
        // trip: 0 on create — omit it; the serializer links it via the trip FK on create
        ...(data.id > 0 ? { trip: data.id } : {}),
        custom_field: field.id,
        value: data.custom_fields?.[idx]?.value
      }
    });
    const trip = {
      ...data,
      vehicle_number: normalizedVehicleNumber,
      custom_field_values,
      // Strip frontend-only / read-only fields from consignments before sending to API.
      // Also strip id:0 from consignments (0 means "unsaved") and from groups.
      consignment_groups: (data.consignment_groups || []).map(g => {
        const { id: gId, ...gRest } = g;
        return {
          ...(gId > 0 ? { id: gId } : {}),  // only send group id if it's a real saved id
          ...gRest,
          consignments: (g.consignments || []).map(({ tmp_id, documents, id: cId, ...c }) => ({
            ...(cId > 0 ? { id: cId } : {}),  // only send consignment id if it's a real saved id
            ...c,
          })),
        };
      }),
      assigned_vehicle_id: data.assigned_vehicle_id ? Number(data.assigned_vehicle_id) : null,
      driver_id: data.driver_id ? Number(data.driver_id) : null,
      requested_vehicle_id: data.requested_vehicle_id > 0 ? data.requested_vehicle_id : null,
      vehicle_type_id: data.vehicle_type_id > 0 ? data.vehicle_type_id : null,
      client_id: data.client_id > 0 ? data.client_id : undefined,
      customer_id: data.customer_id > 0 ? data.customer_id : undefined,
      booking_date: normalizeEmpty(data.booking_date),
      booking_time: normalizeEmpty(data.booking_time),
      expected_pickup_date: normalizeEmpty(data.expected_pickup_date),
      expected_pickup_time: normalizeEmpty(data.expected_pickup_time),
      placement_pickup_date: normalizeEmpty(data.placement_pickup_date),
      placement_pickup_time: normalizeEmpty(data.placement_pickup_time),
      actual_in_pickup_date: normalizeEmpty(data.actual_in_pickup_date),
      actual_in_pickup_time: normalizeEmpty(data.actual_in_pickup_time),
      actual_out_pickup_date: normalizeEmpty(data.actual_out_pickup_date),
      actual_out_pickup_time: normalizeEmpty(data.actual_out_pickup_time),
      expected_delivery_date: normalizeEmpty(data.expected_delivery_date),
      expected_delivery_time: normalizeEmpty(data.expected_delivery_time),
      actual_in_delivery_date: normalizeEmpty(data.actual_in_delivery_date),
      actual_in_delivery_time: normalizeEmpty(data.actual_in_delivery_time),
      actual_out_delivery_date: normalizeEmpty(data.actual_out_delivery_date),
      actual_out_delivery_time: normalizeEmpty(data.actual_out_delivery_time),
      loading_supervisor_id: supervisorId > 0 ? supervisorId : null,
      loading_supervisor_name: normalizeEmpty(data.loading_supervisor_name),
      loading_supervisor_contact: normalizeEmpty(data.loading_supervisor_contact),
    };
    const keysToRemove = [
      'assigned_vehicle',
      'branch',
      'customer',
      'client',
      'requested_vehicle',
      'vehicle_type',
      'driver',
      'type_other',
      'from',
      'to',
      'booking_datetime',
      'expected_pickup',
      'placement_datetime',
      'actual_in_pickup',
      'actual_out_pickup',
      'expected_delivery',
      'actual_in_delivery',
      'actual_out_delivery',
      'custom_fields'
    ];
    keysToRemove.forEach(k => delete trip[k]);
    return trip;
  };

  const validate = (data) => {
    const newErrors = {};
    const normalizedVehicleNumber = String(data.vehicle_number || "")
      .toUpperCase()
      .replace(/\s+/g, "");
    if (normalizedVehicleNumber && !VEHICLE_NUMBER_REGEX.test(normalizedVehicleNumber)) newErrors.vehicle_number = 'Invalid vehicle number';
    if (data.driver_contact && !/^\d{10}$/.test(data.driver_contact)) newErrors.driver_contact = 'Invalid phone number';
    const supervisorId = Number(data.loading_supervisor_id);
    if (supervisorId === -1 && !data.loading_supervisor_name) newErrors.loading_supervisor_name = "Supervisor name is required";
    if (supervisorId === -1 && !data.loading_supervisor_contact) newErrors.loading_supervisor_contact = "Supervisor contact is required";
    if (data.loading_supervisor_contact && !/^\d{10}$/.test(data.loading_supervisor_contact)) newErrors.loading_supervisor_contact = 'Invalid phone number';
    return newErrors;
  };

  const onSubmit = async (data) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: 'Save Trip',
      message: `Are you sure you want to save the trip?`,
      onConfirm: async (ce) => {
        ce.preventDefault();
        await handleTripSubmission(data);
        setConfirmModalParams(p => ({ ...p, open: false }));
      }
    });
  };

  const handleTripSubmission = async (data) => {
    data.charges = data.charges.map(row => ({
      ...row,
      total: row.unit * row.price_per_unit
    }));
    setValidationErrors([]);
    const errors = validate(data);
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    setErrors([]);
    const trip = cleanupInputData(data);
    const is_updating = (trip.id && trip.id > 0);
    const [err, response] = await saveData(trip);
    if (err) {
      setErrors(err);
      const errorStepIndex = resolveStepIndexFromErrors(err);
      if (errorStepIndex > -1 && errorStepIndex !== activeStep) {
        setActiveStep(errorStepIndex);
      }
      showBackendErrorToast(err);
    } else {
      if (is_updating) {
        await drainPendingDocUploads(response.id, response.consignment_groups);
        onSave(response);
        // For updates, reset to server response to keep form in sync
        setInitialData(response);
        resetFromServerResponse(response);
        dirtyRef.current = false;
        setIsDirty(false);
      } else {
        // New trip created — reset to empty for next entry
        const headerValues = {
          branch_id: getValues("branch_id"),
          customer_id: getValues("customer_id"),
          client_id: getValues("client_id"),
          type: getValues("type"),
          type_other: getValues("type_other"),
        };
        reset({
          ...defaultValues,
          ...headerValues,
        });
        setTripCreated(false);
        setActiveStep(0);
        dirtyRef.current = false;
        setIsDirty(false);
      }
    }
  };

  const saveData = async (tripData) => {
    let err, response;
    setLoading(true);
    if (tripData.id && tripData.id > 0) {
      const changedFields = getChangedFields(initialData, tripData);
      if (Object.keys(changedFields).length === 0) {
        return;
      }
      [err, response] = await updateTripPartial(tripData.id, changedFields);
    } else {
      tripData.is_active = 1;
      // Remove server-generated fields from create payload.
      // Sending id:0 triggers a unique constraint error; trip_code is auto-generated.
      delete tripData.id;
      delete tripData.trip_code;
      [err, response] = await createTrip(tripData);
    }
    setLoading(false);
    return [err, response];
  };

  const toggleClient = (client) =>
    setExpandedClients((prev) => ({
      ...prev,
    [client]: !prev[client],
  }));

  // const selectedCustomer = customers.find(c => c.id === customer_id);
  const getSelectedCustomer = () => {
    const customerId = getValues("customer_id");
    if (!customerId) return null;

    // Always search from FULL customers list, not filtered
    return customers.find(c => c.id === Number(customerId)) || null;
  };
  const getSelectedClient = () => {
    const clientId = getValues("client_id");
    if (!clientId) return null;

    // Always search from FULL clients list, not filtered
    return clients.find(c => c.id === Number(clientId)) || null;
  };

  const selectedCustomer = getSelectedCustomer();
  const selectedClient = getSelectedClient();
  const clientLocations = Array.isArray(selectedClient?.locations)
  ? selectedClient.locations
  : [];
  const locations = selectedCustomer
    ? [
        // include the selected customer
        {
          ...selectedCustomer,
          tid: `cst_${selectedCustomer.id}`,
          code: selectedCustomer.customer_code,
          full_address: `${selectedCustomer.short_code} | ${[
            selectedCustomer.address_line1,
            selectedCustomer.address_line2,
            selectedCustomer.city,
            selectedCustomer.state,
            selectedCustomer.pincode,
          ].filter(Boolean).join('\n')}`,
        },
        // include only clients whose IDs are in the customer's client_ids
        ...clientLocations
          .map(cl => ({
            ...cl,
            tid: `cl_${cl.id}`,
            code: selectedClient?.client_code,
            full_address: `${cl.short_code || 'NA'} | ${[
              cl.address_line1,
              cl.address_line2,
              cl.city,
              cl.state,
              cl.pincode,
            ].filter(Boolean).join('\n')}`,
          })),
      ]
    : [];

  /** After a trip save, match pending staged files to their now-real consignment IDs and upload. */
  const drainPendingDocUploads = async (tripId, savedGroups) => {
    const pending = pendingDocUploads.current;
    if (Object.keys(pending).length === 0) return;
    const uploads = [];
    for (const group of (savedGroups || [])) {
      for (const c of (group.consignments || [])) {
        if (c.tmp_id && pending[c.tmp_id]?.length > 0 && c.id > 0) {
          uploads.push(uploadConsignmentDocuments(tripId, c.id, pending[c.tmp_id]));
          delete pending[c.tmp_id];
        }
      }
    }
    if (uploads.length > 0) await Promise.all(uploads);
  };

  /**
   * Reset the form from a raw server trip response, mapping nested objects to
   * their write-only id fields (client_id, customer_id, etc.).
   * Use this instead of bare reset(serverData) so FK dropdowns stay populated.
   */
  const resetFromServerResponse = (tripData) => {
    const customer = customers.find(c => c.id === Number(tripData?.customer?.id));
    const hasManualSupervisorDetails = Boolean(
      tripData?.loading_supervisor_name || tripData?.loading_supervisor_contact
    );
    const resolvedSupervisorId = showLoadingSupervisor
      ? (tripData?.loading_supervisor?.id ?? (hasManualSupervisorDetails ? -1 : 0))
      : 0;
    reset({
      ...defaultValues,
      ...tripData,
      branch_id: tripData?.branch?.id || profile?.branch?.id || 0,
      loading_supervisor_id: resolvedSupervisorId,
      loading_supervisor_name: (showLoadingSupervisor || isBranchUser) ? (tripData?.loading_supervisor_name ?? tripData?.loading_supervisor?.name ?? profile?.name ?? "") : "",
      loading_supervisor_contact: (showLoadingSupervisor || isBranchUser) ? (tripData?.loading_supervisor_contact ?? tripData?.loading_supervisor?.phone ?? profile?.phone ?? "") : "",
      type: tripData?.type || "",
      customer_id: tripData?.customer?.id || 0,
      client_id: tripData?.client?.id || 0,
      driver_id: String(tripData?.driver?.id) || "",
      requested_vehicle_id: tripData?.requested_vehicle?.id || 0,
      vehicle_type_id: tripData?.vehicle_type?.id || 0,
      assigned_vehicle_id: String(tripData?.assigned_vehicle?.id) || "",
      booking_date: tripData?.booking_date ?? today,
      booking_time: tripData?.booking_time ?? timeNow,
      placement_pickup_date: tripData?.placement_pickup_date ?? today,
      placement_pickup_time: tripData?.placement_pickup_time ?? timeNow,
      expected_pickup_date: tripData?.expected_pickup_date ?? "",
      expected_pickup_time: tripData?.expected_pickup_time ?? "",
      expected_delivery_date: tripData?.expected_delivery_date ?? "",
      expected_delivery_time: tripData?.expected_delivery_time ?? "",
      actual_in_pickup_date: tripData?.actual_in_pickup_date ?? "",
      actual_in_pickup_time: tripData?.actual_in_pickup_time ?? "",
      actual_out_pickup_date: tripData?.actual_out_pickup_date ?? "",
      actual_out_pickup_time: tripData?.actual_out_pickup_time ?? "",
      actual_in_delivery_date: tripData?.actual_in_delivery_date ?? "",
      actual_in_delivery_time: tripData?.actual_in_delivery_time ?? "",
      actual_out_delivery_date: tripData?.actual_out_delivery_date ?? "",
      actual_out_delivery_time: tripData?.actual_out_delivery_time ?? "",
      from: tripData?.from_location?.tid,
      to: tripData?.to_location?.tid,
      custom_fields: customer ? customFieldsWithValues(customer) : [],
      charges: markFixed(
        (tripData?.charges && tripData.charges.length > 0)
          ? tripData.charges
          : defaultCharges
      ),
    });
    onBranchChange(
      tripData?.branch?.id || profile?.branch?.id,
      tripData?.customer?.id
    );
  };

  const uploadClientManifest = async (client_id, manifest) => {
    setErrors([]);
    const [saveDataErr] = await saveData(cleanupInputData(getValues()));
    if (saveDataErr) {
      setErrors(saveDataErr);
      return;
    }
    const tripId = trip?.id || formTripId;
    let [err, response] = await uploadManifest(tripId, { client_id, manifest });
    if (!err) {
      [err, response] = await getTrip(tripId);
      if (response) {
        await drainPendingDocUploads(tripId, response.consignment_groups);
        const [, fresh] = await getTrip(tripId);
        const data = fresh ?? response;
        setInitialData(data);
        resetFromServerResponse(data);
        dirtyRef.current = false;
        setIsDirty(false);
      }
    }
  };

  /** Upload one or more files to a saved consignment. Requires trip to be saved first. */
  const handleUploadConsignmentDocs = async (consignmentId, files) => {
    const tripId = trip?.id || formTripId;
    if (!tripId) {
      toast.error("Save the trip first before uploading documents.");
      return;
    }
    if (!consignmentId || consignmentId <= 0) {
      toast.error("Save the consignment first before uploading documents.");
      return;
    }
    const [err] = await uploadConsignmentDocuments(tripId, consignmentId, files);
    if (!err) {
      const [, updated] = await getTrip(tripId);
      if (updated) {
        setInitialData(updated);
        resetFromServerResponse(updated);
        dirtyRef.current = false;
        setIsDirty(false);
      }
    }
  };

  /** Download a single document — presigned URL (S3) or direct stream (local). */
  const handleDownloadConsignmentDoc = async (docId, filename) => {
    const tripId = trip?.id || formTripId;
    const [err, response] = await getConsignmentDocumentDownloadUrl(tripId, docId);
    if (err) return;
    if (response?.url) {
      // S3 presigned URLs are cross-origin — the `download` attribute is ignored
      // by browsers on cross-origin links. Open in a new tab instead; the browser
      // will either display (PDF/images) or download the file automatically.
      window.open(response.url, '_blank', 'noopener,noreferrer');
    }
  };

  /** Delete a document from the server then refresh the form. */
  const handleDeleteConsignmentDoc = async (docId) => {
    const tripId = trip?.id || formTripId;
    const [err] = await deleteConsignmentDocument(tripId, docId);
    if (!err) {
      const [, updated] = await getTrip(tripId);
      if (updated) {
        setInitialData(updated);
        resetFromServerResponse(updated);
        dirtyRef.current = false;
        setIsDirty(false);
      }
    }
  };

  const downloadManifest = async (client_id, file) => {
    if (file instanceof File) {
      // File is staged locally (not yet saved) — download directly from memory.
      downloadLocalFile(file);
      return;
    }

    if (typeof file === "string") {
      // File is stored on the server (local disk or S3).
      // Always go through the authenticated download endpoint so:
      //   - local files are streamed with auth
      //   - S3 files get a short-lived presigned URL without exposing credentials
      const consignments = getValues().consignment_groups?.filter((c) => c.client == client_id);
      if (!consignments?.length) return;

      const [err, response] = await getManifestDownloadUrl(trip?.id || formTripId, client_id);
      if (err) return;

      if (response?.url) {
        // S3 mode — open presigned URL in a new tab.
        window.open(response.url, '_blank', 'noopener,noreferrer');
      } else {
        // Local storage mode — server streamed the file; shouldn't normally
        // reach here via apiWrapper, but handle just in case.
        const fileUrl = consignments[0]?.manifest;
        if (!fileUrl) return;
        downloadFile(fileUrl, fileUrl.split("/").pop());
      }
    }
  };

  const onAddConsignment = (e) => {
    e.preventDefault();
    setEditingConsigment(null);
    // Pre-select the client already chosen on the trip so the consignment
    // modal opens with that client pre-populated.
    setEditingClientId(getValues("client_id") || null);
    setShowConsignmentModal(true);
  };

  const onEditConsignment = (e, clientId, consignment) => {
    e.preventDefault();
    setEditingConsigment(consignment);
    setEditingClientId(clientId);
    setShowConsignmentModal(true);
  };

  const onDeleteConsignment = (e, clientId, consignment) => {
    e.preventDefault();
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: 'Delete Consignment',
      message: `Are you sure you want to delete the consignment?`,
      onConfirm: (ce) => {
        ce.preventDefault();
        handleDeleteConsignment(clientId, consignment);
      }
    });
  }

  const handleDeleteConsignment = (clientId, consignment) => {
    const groups = watch('consignment_groups') || [];
    const updatedGroups = groups.map(group => {
      if (group.client === clientId) {
        return {
          ...group,
          consignments: group.consignments.filter(c => {
            if (consignment.id > 0) {
              return c.id !== consignment.id;
            }
            return c.tmp_id !== consignment.tmp_id;
          })
        };
      }
      return group;
    });
    setValue("consignment_groups", updatedGroups, { shouldDirty: true });
    setConfirmModalParams({...confirmModalParams, open: false});
  };

  const renderStep = () => {
    switch (FORM_STEPS[activeStep].id) {

      case "vehicle":
        return <VehicleSection
            register={register}
            watch={watch}
            control={control}
            vehicleModes={vehicleModes}
            vehicleTypes={vehicleTypes}
            filteredVehicles={filteredVehicles}
            validationErrors={validationErrors}
            errors={errors}
          />;

      case "driver":
        return <DriverSection
            register={register}
            watch={watch}
            driverModes={driverModes}
            filteredDrivers={filteredDrivers}
            validationErrors={validationErrors}
            errors={errors}
          />;

      case "location":
        return <LocationSection
            register={register}
            watch={watch}
            locations={locations}
            errors={errors}
          />;

      case "timeline":
        return <TimelineSection
            register={register}
            watch={watch}
            errors={errors}
          />;

      case "requestor":
        return <RequestorSection
            register={register}
            watch={watch}
            errors={errors}
            loadingSupervisors={loading_supervisors}
            validationErrors={validationErrors}
          />;

      case "consignment":
        return <ConsignmentSection
            register={register}
            watch={watch}
            getValues={getValues}
            setValue={setValue}
            setExpandedClients={setExpandedClients}
            setManifestFiles={setManifestFiles}
            onAddConsignment={onAddConsignment}
            downloadManifest={downloadManifest}
            expandedClients={expandedClients}
            toggleClient={toggleClient}
            onDeleteConsignment={onDeleteConsignment}
            onEditConsignment={onEditConsignment}
            uploadClientManifest={uploadClientManifest}
            onUploadConsignmentDocs={handleUploadConsignmentDocs}
            onDownloadConsignmentDoc={handleDownloadConsignmentDoc}
            onDeleteConsignmentDoc={handleDeleteConsignmentDoc}
            tripSaved={!!trip?.id || formTripId > 0}
            errors={errors}
          />;

      case "billing":
        return <BillingSection
            register={register}
            watch={watch}
            billingFields={billingFields}
            appendBilling={appendBilling}
            removeBilling={removeBilling}
            totalCharge={totalCharge}
            errors={errors}
          />;

      case "custom_data":
        return <CustomDataSection
            customFields={customFields}
            errors={errors}
          />;

      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      {/* Read-only banner — outside the form so it doesn't affect form layout */}
      {readOnly && (
        <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm px-4 py-2 rounded mb-2">
          This trip is <strong className="capitalize">{trip?.status}</strong> and cannot be edited.
        </div>
      )}
      <form
        noValidate
        onInputCapture={handleUppercaseInputCapture}
        onSubmit={handleSubmit(onSubmit)}
        className="p-4 bg-white rounded shadow space-y-6 overflow-visible uppercase [&_input[type='text']]:uppercase [&_textarea]:uppercase [&_select]:uppercase [&_input:disabled]:uppercase"
      >
        <fieldset disabled={readOnly} className="contents">
          <TripDetailsSection
            register={register}
            watch={watch}
            setValue={setValue}
            tripCreated={tripCreated}
            setTripCreated={setTripCreated}
            customers={filteredCustomers}
            clients={clients}
            branches={branches}
            onBranchChange={onBranchChange}
            tripTypes={tripTypes}
            isEditMode={isEditMode}
            role={role}
            errors={errors}
            isInitializingRef={isInitializingRef}
          />
        </fieldset>

        {/* FormStepper is outside the fieldset so step buttons remain clickable in read-only mode */}
        {tripCreated && (
          <div className="relative w-full mt-4 mb-2 bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-4">
            <FormStepper
              steps={FORM_STEPS}
              activeStep={activeStep}
              onChange={setActiveStep}
            />
          </div>
        )}

        {tripCreated && (
          <>
            <fieldset disabled={readOnly} className="contents">
              <div className="rounded-xl p-4 bg-white shadow-sm border border-gray-100">
                {renderStep()}
              </div>
            </fieldset>

            {/* Navigation — always enabled so steps are browsable in read-only mode too */}
            <div className="flex justify-end gap-3 mt-4">
              {(activeStep != 0) &&
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={activeStep === 0}
                  className="flex-1 md:flex-none px-4 py-2 bg-gray-200 rounded disabled:opacity-50 text-sm"
                >
                  Back
                </button>
              }
              {(activeStep != (FORM_STEPS.length - 1)) &&
                <button
                  type="button"
                  disabled={activeStep == (FORM_STEPS.length - 1)}
                  onClick={nextStep}
                  className="flex-1 md:flex-none px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 text-sm"
                >
                  Next
                </button>
              }
            </div>

            {!readOnly && (
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="submit"
                  className="w-full md:w-auto px-4 py-2 bg-green-600 text-white rounded text-sm font-medium"
                >
                  Save Trip
                </button>
              </div>
            )}
          </>
        )}

        {/* Consignment Modal */}
        {showConsignmentModal && (
          <ConsignmentModal
            clients={filteredClients.length > 0 ? filteredClients : clients.filter(cl => selectedCustomer?.clients?.includes(cl.id))}
            consignment={editingConsigment}
            selectedClient={editingClientId}
            onSave={handleConsignmentAdd}
            onClose={() => setShowConsignmentModal(false)}
            tripSaved={true}
            onUploadConsignmentDocs={handleUploadConsignmentDocs}
            onDownloadConsignmentDoc={handleDownloadConsignmentDoc}
            onDeleteConsignmentDoc={handleDeleteConsignmentDoc}
          />
        )}
        <ConfirmationModal
          open={confirmModalParams.open}
          title={confirmModalParams.title}
          message={confirmModalParams.message}
          onConfirm={confirmModalParams.onConfirm}
          onCancel={confirmModalParams.onCancel}
          confirmText={confirmModalParams.confirmText}
          cancelText={confirmModalParams.cancelText}
        />
      </form>
    </FormProvider>
  );
};

export default TripForm;
